import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { getTextField } from '@lender/utils';
import { Box, Button, TableCell, TableRow } from '@mui/material';
import { ArrowCheckIcon, InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import { loanConditionApiV2 } from 'app/services/api';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import LoanConditionInfoTable from 'app/shared/modules/LoanConditionInfoTable';
import LoanConditionTasksTable from 'app/shared/modules/LoanConditionTasksTable';
import RejectConditionForm from 'app/shared/modules/RejectConditionForm';
import dayjs from 'dayjs';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { object } from 'yup';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { formatPriorToDisplayName } from 'constants/condition';
import { IconDirection } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { BrokerConditionStatusEnum, SignOffStatusOptions } from 'constants/loanActionEditor';
import { PAGE_CODES } from 'constants/pageCodes';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';
import { logger } from 'utils/logger';
import { findPriorToOption, getPriorToPickerOptions, getPriorToRequestFields } from 'utils/priorTo';

import {
  getMasterPermissionsToPage,
  selectIsBrokerCompany,
  selectIsClaimAllowed,
  selectIsSubCompanyUser,
} from 'store/auth/selectors';
import { openDialogAction, showMessageAction } from 'store/global/actions';
import {
  deleteLoanConditionAction,
  getLoanBrokerConditionByApplicationIdAction,
  getLoanConditionByLoanIdAction,
  signOffLoanConditionAction,
  unsignOffLoanConditionAction,
  updateBrokerConditionAction,
} from 'store/loanApplications/actions';
import {
  selectBrokerTasksByConditionId,
  selectIsLoanCanceled,
  selectLoanConditionById,
  selectTasksByIds,
} from 'store/loanApplications/selectors';
import { selectPriorTo, selectPriorToAllOptions } from 'store/underwriting/selectors';

import { UnderwritingTestIds } from '../../../Underwriting.testIds';

const getStyles = ({ isCollapsed, isRelatedTasks, isDeleted } = {}) => ({
  dynamicCell: {
    display: !isCollapsed ? 'none' : 'table-cell',
  },
  row: {
    cursor: isRelatedTasks && !isDeleted ? 'pointer' : 'default',
    '& .MuiTableCell-root': {
      padding: '16px 10px',
      height: '80px',
    },
  },
  buttonsCell: {
    whiteSpace: 'nowrap',
  },
  collapseIcon: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 0',
  },
  messageInput: {
    position: 'relative',
    '& p': {
      content: '',
      position: 'absolute',
      top: '100%',
      left: '-14px',
      width: '100%',
      height: '100%',
    },
  },
});

const RequestTypes = {
  SIGN_OFF: 'SignOff',
  UPDATE: 'PropertiesUpdate',
};

const schema = object().shape({
  loanConditionId: getTextField(),
  priorToType: getTextField({ isRequired: false }).nullable(),
  message: getTextField({ isRequired: true }).nullable(),
  subCompanyMessage: getTextField({ isRequired: false }).nullable(),
  borrowerMessage: getTextField({ isRequired: false }).nullable(),
});

const LoanConditionTableItem = ({
  number,
  message,
  subCompanyMessage,
  borrowerMessage,
  createdBy,
  createdDate,
  signedOff = '',
  signedOffBy = '',
  id,
  setIsLoading,
  isLoading,
  canBeSignedOff,
  canBeUnsignedOff,
  canBeRejected,
  rejectedDate,
  rejectionReason = '',
  isDeleted,
  slaGroup,
  taskIds,
  priorToType,
  priorToTypeId = null,
  priorToTypeName = null,
  status,
  withDeletedDateCell,
  withRejectedDateCell,
  withSignedOffCell,
  withActionsCell,
  withCollapseCell,
  lastModified,
  taskActionRunnerContext = null,
  onTaskActionRefresh = null,
}) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const { applicationId } = useParams();

  const isBrokerCompany = useSelector(selectIsBrokerCompany);
  const isSubCompanyUser = useSelector(selectIsSubCompanyUser);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const lenderTasks = useSelector(selectTasksByIds(taskIds));
  const brokerTasks = useSelector(selectBrokerTasksByConditionId(id));
  const priorToOptions = useSelector(selectPriorTo);
  const allPriorToOptions = useSelector(selectPriorToAllOptions);
  const relatedTasks = isBrokerCompany ? brokerTasks : lenderTasks;
  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_CONDITIONS));
  const isSignOffAllowed = useSelector(selectIsClaimAllowed(PAGE_CODES.LOAN_CONDITIONS_SIGN_OFF));
  const isUnsignOffAllowed = useSelector(
    selectIsClaimAllowed(PAGE_CODES.LOAN_CONDITIONS_UNSIGN_OFF),
  );
  const isRejectAllowed = useSelector(selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_CONDITIONS_REJECT));
  const isOwnRejectAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_CONDITIONS_OWN_REJECT),
  );

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const isRelatedTasks = isBrokerCompany ? !!brokerTasks.length : !!taskIds?.length;
  const isRejected = !!rejectedDate;
  const isTerminal = isDeleted || isRejected;
  const classes = getStyles({ isCollapsed, isRelatedTasks, isDeleted, isEdit });

  const isRejectActionAllowed = isRejectAllowed || isOwnRejectAllowed;
  const isSignOffAvailable = !signedOff && canBeSignedOff && !isTerminal;
  const isUnsignOffAvailable =
    signedOff && canBeUnsignedOff && (isUnsignOffAllowed || isManageAllowed) && !isTerminal;
  const isRejectAvailable =
    !signedOff && canBeRejected && isRejectActionAllowed && !isTerminal && !isBrokerCompany;
  const isDeleteAvailable = isManageAllowed && isSignOffAllowed && !isTerminal;
  const isEditAvailable = !isTerminal;

  const statusText = isBrokerCompany ? status.name : signedOffBy;

  const currentPriorToOption = useMemo(
    () => findPriorToOption({ options: allPriorToOptions, priorToTypeId, priorToType }),
    [allPriorToOptions, priorToTypeId, priorToType],
  );

  const priorToPickerOptions = useMemo(
    () => getPriorToPickerOptions({ options: priorToOptions, currentOption: currentPriorToOption }),
    [priorToOptions, currentPriorToOption],
  );

  const priorToDisplayName =
    priorToTypeName ?? currentPriorToOption?.name ?? formatPriorToDisplayName(priorToType);

  const initialValues = useMemo(
    () => ({
      loanConditionId: id,
      priorToType: currentPriorToOption,
      message,
      subCompanyMessage,
      status,
      borrowerMessage,
    }),
    [borrowerMessage, id, message, subCompanyMessage, currentPriorToOption, status],
  );

  const {
    form,
    setInForm,
    setSchema,
    validateField,
    validationErrors,
    validateForm,
    setForm,
    isFormChanged,
    handleChange,
  } = useForm(initialValues);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onSignOff = useCallback(
    e => {
      e.stopPropagation();

      setIsLoading(true);
      dispatch(
        isBrokerCompany
          ? updateBrokerConditionAction({ loanConditionId: id, type: RequestTypes.SIGN_OFF })
          : signOffLoanConditionAction({ loanConditionId: id }),
      )
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [setIsLoading, dispatch, isBrokerCompany, id],
  );

  const onUnsignOff = useCallback(
    e => {
      e.stopPropagation();

      setIsLoading(true);
      dispatch(unsignOffLoanConditionAction({ loanConditionId: id }))
        .then(() =>
          dispatch(
            getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true }),
          ),
        )
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [setIsLoading, dispatch, id, loanId],
  );

  const rejectCondition = useMemo(() => ({ id, number }), [id, number]);

  const onReject = useCallback(
    e => {
      e.stopPropagation();

      dispatch(
        openDialogAction({
          header: `Reject Condition #${number}`,
          size: ModalSize.SMALL,
          body: () => (
            <RejectConditionForm
              condition={rejectCondition}
              isBrokerCompany={isBrokerCompany}
              loanId={loanId}
            />
          ),
        }),
      );
    },
    [dispatch, isBrokerCompany, loanId, number, rejectCondition],
  );

  const onDelete = useCallback(
    e => {
      e.stopPropagation();

      if (isBrokerCompany) {
        return;
      }

      setIsLoading(true);
      dispatch(deleteLoanConditionAction({ loanConditionId: id, taskIds }))
        .then(() => setIsCollapsed(false))
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [setIsLoading, dispatch, isBrokerCompany, id, taskIds],
  );

  const onInfoClick = useCallback(
    e => {
      e.stopPropagation();

      dispatch(getLoanConditionByLoanIdAction({ loanId, withDeleted: true }));

      dispatch(
        openDialogAction({
          header: `${slaGroup?.name} Condition SLA Group`,
          size: ModalSize.LARGE,
          body: dialogProps => (
            <LoanConditionInfoTable
              loanConditionByIdSelector={selectLoanConditionById}
              loanConditionId={id}
              {...dialogProps}
            />
          ),
        }),
      );
    },
    [dispatch, id, slaGroup?.name, loanId],
  );

  const onRejectionReasonClick = useCallback(
    e => {
      e.stopPropagation();

      dispatch(
        openDialogAction({
          header: `Rejection Reason for Condition #${number}`,
          size: ModalSize.SMALL,
          body: () => <Box className="whitespace-pre-wrap break-words p-16">{rejectionReason}</Box>,
        }),
      );
    },
    [dispatch, number, rejectionReason],
  );

  const onEdit = useCallback(
    e => {
      e.stopPropagation();

      setIsEdit(prev => !prev);
    },
    [setIsEdit],
  );

  const onCancel = useCallback(
    e => {
      e.stopPropagation();

      setForm(initialValues);
      setIsEdit(false);
    },
    [initialValues, setForm],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();
      e.stopPropagation();

      if (isBrokerCompany && !(await validateForm())) {
        return;
      }

      const dataToSend = isBrokerCompany
        ? {
            type: `${RequestTypes.UPDATE}.Broker`,
            internalText: form.message?.toString(),
            borrowerInternalText: form.borrowerMessage?.toString() || null,
            ...getPriorToRequestFields(form.priorToType),
            loanConditionId: form.loanConditionId,
            status: form.status.value,
            subCompanyInternalText: form.subCompanyMessage?.toString() || null,
          }
        : {
            loanConditionsPriorToTypes: [
              {
                loanConditionId: form.loanConditionId,
                ...getPriorToRequestFields(form.priorToType),
              },
            ],
          };

      setIsLoading(true);

      const action = isBrokerCompany
        ? loanConditionApiV2.editBrokerCondition(dataToSend)
        : loanConditionApiV2.editConditionPriorToType(dataToSend);

      action
        .then(() =>
          dispatch(
            isBrokerCompany
              ? getLoanBrokerConditionByApplicationIdAction({ applicationId })
              : getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true }),
          ),
        )
        .catch(err => {
          setForm(initialValues);
          dispatch(showMessageAction({ message: 'Error while saving form', variant: 'error' }));
          logger.error(err);
        })
        .finally(() => {
          setIsEdit(false);
          setIsLoading(false);
        });
    },
    [
      isBrokerCompany,
      validateForm,
      form,
      setIsLoading,
      dispatch,
      applicationId,
      loanId,
      setForm,
      initialValues,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const handleCollapse = useCallback(() => {
    if (!isRelatedTasks || isDeleted) {
      return;
    }

    setIsCollapsed(prevIsCollapsed => !prevIsCollapsed);
  }, [isRelatedTasks, isDeleted]);

  const handlePropagation = useCallback(e => e.stopPropagation(), []);

  const renderPriorToAutocomplete = useMemo(
    () => (
      <AutocompleteCDS
        disableClearable
        fullWidth
        required
        className="m-0"
        dataTestIdInput={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_PRIOR_TO_AUTOCOMPLETE_SELECT}
        dataTestIdOption={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_PRIOR_TO_OPTION}
        disabled={isLoading || signedOff}
        label="Prior To"
        name="priorToType"
        options={priorToPickerOptions}
        value={form.priorToType}
        variant="outlined"
        withSortedOptions={false}
        onChange={onAutocompleteChange}
      />
    ),
    [isLoading, signedOff, priorToPickerOptions, form, onAutocompleteChange],
  );

  const renderStatusToAutocomplete = useMemo(() => {
    const statusOptions = canBeSignedOff
      ? SignOffStatusOptions
      : SignOffStatusOptions.filter(({ value }) => value !== BrokerConditionStatusEnum.SIGNED_OFF);

    return (
      <AutocompleteCDS
        disableClearable
        fullWidth
        required
        className="m-0"
        dataTestIdInput={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_STATUS_SELECT}
        dataTestIdOption={UnderwritingTestIds.ADD_BROKER_CONDITION_FORM_STATUS_OPTION}
        disabled={isLoading || signedOff}
        label="Status"
        name="status"
        optionName="name"
        options={statusOptions}
        value={form.status}
        variant="outlined"
        onChange={onAutocompleteChange}
      />
    );
  }, [canBeSignedOff, isLoading, signedOff, form.status, onAutocompleteChange]);

  const renderMessageInput = useMemo(
    () => (
      <TextFieldCDS
        required
        dataTestId={UnderwritingTestIds.BROKER_CONDITION_FORM_MESSAGE_INPUT}
        disabled={isLoading || signedOff}
        error={!!validationErrors.message}
        helperText={validationErrors.message}
        label="Message"
        name="message"
        sx={classes.messageInput}
        value={form.message}
        variant="outlined"
        onChange={handleChange}
      />
    ),
    [
      classes.messageInput,
      form.message,
      handleChange,
      isLoading,
      signedOff,
      validationErrors.message,
    ],
  );

  const renderInternalTextInput = useMemo(
    () => (
      <TextFieldCDS
        dataTestId={UnderwritingTestIds.BROKER_CONDITION_FORM_INTERNAL_TEXT_INPUT}
        disabled={isLoading || signedOff}
        label="Internal Text"
        name="borrowerMessage"
        value={form.borrowerMessage}
        variant="outlined"
        onChange={handleChange}
      />
    ),
    [form.borrowerMessage, handleChange, isLoading, signedOff],
  );

  const messageCellContent = useMemo(() => {
    if (isEdit && isBrokerCompany) {
      return renderMessageInput;
    }

    if (isSubCompanyUser) {
      return subCompanyMessage;
    }

    return message;
  }, [isEdit, isBrokerCompany, isSubCompanyUser, renderMessageInput, subCompanyMessage, message]);

  return (
    <>
      <TableRow data-testid={id} sx={classes.row} onClick={handleCollapse}>
        {!isBrokerCompany && (
          <TableCell align="left" onClick={handlePropagation}>
            {number}
          </TableCell>
        )}
        <TableCell align="left" onClick={handlePropagation}>
          {messageCellContent}
        </TableCell>
        {isBrokerCompany && (
          <TableCell align="left" onClick={handlePropagation}>
            {isEdit ? renderInternalTextInput : borrowerMessage}
          </TableCell>
        )}
        <TableCell align="left" sx={classes.priorToCell} onClick={handlePropagation}>
          {isEdit ? renderPriorToAutocomplete : priorToDisplayName}
        </TableCell>
        <TableCell align="left">{createdBy}</TableCell>
        <TableCell align="left">{dayjs(createdDate).format(COMMON_DATETIME_FORMAT)}</TableCell>
        {withSignedOffCell && (
          <>
            <TableCell align="left">
              {isEdit && isBrokerCompany ? renderStatusToAutocomplete : statusText}
            </TableCell>
            <TableCell align="left">
              {signedOff ? dayjs(signedOff).format(COMMON_DATETIME_FORMAT) : '---'}
            </TableCell>
          </>
        )}
        {withDeletedDateCell && (
          <TableCell align="left">
            {isDeleted ? dayjs(lastModified).format(COMMON_DATETIME_FORMAT) : '---'}
          </TableCell>
        )}
        {withRejectedDateCell && (
          <TableCell align="left">
            {isRejected ? (
              <div className="flex items-center gap-8">
                <span>{dayjs(rejectedDate).format(COMMON_DATETIME_FORMAT)}</span>
                {!!rejectionReason && (
                  <ButtonWithTooltip
                    data-testid={UnderwritingTestIds.BROKER_CONDITION_FORM_REJECTION_REASON_BUTTON}
                    icon={InfoIcon}
                    iconButtonClassName="!p-0"
                    title="Rejection Reason"
                    onClick={onRejectionReasonClick}
                  />
                )}
              </div>
            ) : (
              '---'
            )}
          </TableCell>
        )}
        {withActionsCell && (
          <TableCell align="left" sx={classes.buttonsCell}>
            {!isTerminal && (
              <div className="flex">
                {!signedOff ? (
                  <div className="flex flex-col gap-8">
                    {!isBrokerCompany && (
                      <>
                        <div className="flex gap-8 items-center">
                          <Button
                            className="flex-1"
                            data-testid={UnderwritingTestIds.BROKER_CONDITION_FORM_SIGN_OFF_BUTTON}
                            disabled={!isSignOffAvailable || isLoanCanceled || isLoading || isEdit}
                            id={id}
                            size="small"
                            variant="contained"
                            onClick={onSignOff}
                          >
                            Sign Off
                          </Button>

                          <ButtonWithTooltip
                            data-testid={UnderwritingTestIds.BROKER_CONDITION_FORM_SLA_INFO_BUTTON}
                            icon={InfoIcon}
                            iconButtonClassName="!p-0"
                            title="SLA Group Info"
                            onClick={onInfoClick}
                          />
                        </div>

                        <div className="flex gap-8">
                          {isRejectAvailable && (
                            <Button
                              className="flex-1"
                              data-testid={UnderwritingTestIds.BROKER_CONDITION_FORM_REJECT_BUTTON}
                              disabled={isLoanCanceled || isLoading || isEdit}
                              id={id}
                              size="small"
                              variant="outlined"
                              onClick={onReject}
                            >
                              Reject
                            </Button>
                          )}

                          <Button
                            className="flex-1"
                            data-testid={UnderwritingTestIds.BROKER_CONDITION_FORM_DELETE_BUTTON}
                            disabled={!isDeleteAvailable || isLoanCanceled || isLoading || isEdit}
                            id={id}
                            size="small"
                            variant="contained"
                            onClick={onDelete}
                          >
                            Delete
                          </Button>
                        </div>
                      </>
                    )}

                    <div className="flex gap-8">
                      <Button
                        className="flex-1"
                        data-testid={UnderwritingTestIds.BROKER_CONDITION_FORM_EDIT_BUTTON}
                        disabled={!isEditAvailable || isLoading || (isEdit && !isFormChanged)}
                        size="small"
                        variant="contained"
                        onClick={isEdit ? onSubmit : onEdit}
                      >
                        {isEdit ? 'Save' : 'Edit'}
                      </Button>
                      {isEdit && (
                        <Button
                          data-testid={UnderwritingTestIds.BROKER_CONDITION_FORM_CANCEL_BUTTON}
                          disabled={isLoading}
                          size="small"
                          variant="outlined"
                          onClick={onCancel}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  !isBrokerCompany &&
                  isUnsignOffAvailable && (
                    <Button
                      data-testid={UnderwritingTestIds.BROKER_CONDITION_FORM_UNSIGN_OFF_BUTTON}
                      disabled={isLoanCanceled || isLoading}
                      id={id}
                      size="small"
                      variant="outlined"
                      onClick={onUnsignOff}
                    >
                      Unsign Off
                    </Button>
                  )
                )}
              </div>
            )}
          </TableCell>
        )}
        {withCollapseCell && (
          <TableCell>
            {!isDeleted && isRelatedTasks && (
              <Box sx={classes.collapseIcon}>
                <ButtonWithTooltip
                  data-testid={UnderwritingTestIds.BROKER_CONDITION_FORM_COLLAPSE_BUTTON}
                  icon={ArrowCheckIcon}
                  iconDirection={isCollapsed ? IconDirection.UP : IconDirection.DOWN}
                  size="small"
                  title={isCollapsed ? 'Collapse' : 'Expand'}
                />
              </Box>
            )}
          </TableCell>
        )}
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={classes.dynamicCell}>
          <LoanConditionTasksTable
            conditionId={id}
            formName={FormNames.CONDITIONS_TASK_INFO}
            taskActionRunnerContext={taskActionRunnerContext}
            tasks={relatedTasks}
            onTaskActionRefresh={onTaskActionRefresh}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

LoanConditionTableItem.propTypes = {
  priorToType: PropTypes.string.isRequired,
  createdBy: PropTypes.string.isRequired,
  createdDate: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  number: PropTypes.number.isRequired,
  slaGroup: PropTypes.object.isRequired,
  withDeletedDateCell: PropTypes.bool.isRequired,
  withRejectedDateCell: PropTypes.bool.isRequired,
  withSignedOffCell: PropTypes.bool.isRequired,
  canBeRejected: PropTypes.bool,
  priorToTypeId: PropTypes.string,
  priorToTypeName: PropTypes.string,
  rejectedDate: PropTypes.string,
  rejectionReason: PropTypes.string,
  signedOff: PropTypes.string,
  signedOffBy: PropTypes.string,
  ...WithLoadingPropTypes,
  taskActionRunnerContext: PropTypes.object,
  onTaskActionRefresh: PropTypes.func,
};

export default compose(withLoading, memo)(LoanConditionTableItem);
